import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthProvider';
import { API_URL } from '../../utils/config';
import EmptyState from './EmptyState';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import OrganizerPagination from './OrganizerPagination';
import OrganizerRenderPaginationNumbers from './OrganizerRenderPaginationNumbers';
import './OrganizersTable.css';

const OrganizersTable = () => {
  const { t } = useTranslation('organizers');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [organizers, setOrganizers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [filteredOrganizers, setFilteredOrganizers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    organizerId: null,
    organizerName: '',
    isDeleting: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const baseUrl = API_URL;

  const getLangFromLocalStorage = () => {
    return localStorage.getItem('language') || 'en';
  };

  const getFullName = (organizer) => {
    if (organizer.name) {
      return organizer.name; // For backward compatibility
    }
    return `${organizer.first_name || ''} ${organizer.family_name || ''}`.trim();
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      // Update URL params
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('page', pageNumber.toString());
      window.history.replaceState(null, '', `?${urlParams.toString()}`);
    }
  };

  // Get page from URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get('page')) || 1;
    setCurrentPage(page);
  }, []);

  // Fetch organizers from API
  const fetchOrganizers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Don't navigate here, let ProtectedRoute handle authentication
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
      });

      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/organizer/all?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'lang': getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        // localStorage.removeItem('user');
        // localStorage.removeItem('token');
        // Let ProtectedRoute handle the redirect instead of navigating here
        setIsLoading(false);
        throw new Error('Unauthorized access. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch organizers: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data); // Debug log
      // Handle different response formats
      const organizersArray = data.data || data.organizers || data || [];
      console.log('Organizers Array:', organizersArray); // Debug log
      setOrganizers(Array.isArray(organizersArray) ? organizersArray : []);
      setFilteredOrganizers(Array.isArray(organizersArray) ? organizersArray : []);

      // Set pagination data
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.totalItems || 0);
      setCurrentPage(data.currentPage || currentPage);

    } catch (error) {
      console.error('Error fetching organizers:', error);
      setError(error.message);
      // For development, you can use sample data as fallback
      if (process.env.NODE_ENV === 'development') {
        const sampleData = [
          {
            id: 1,
            first_name: 'Ahmed',
            family_name: 'Hassan',
            email: 'ahmed.hassan@example.com',
            role: 'manager',
            teamOwner: 'Development Team'
          },
          {
            id: 2,
            first_name: 'Sara',
            family_name: 'Ali',
            email: 'sara.ali@example.com',
            role: 'viewer',
            teamOwner: 'Marketing Team'
          }
        ];
        setOrganizers(sampleData);
        setFilteredOrganizers(sampleData);
        setError(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizers();
    } else {
      // Clear data when not authenticated
      setOrganizers([]);
      setFilteredOrganizers([]);
      setIsLoading(false);
    }
  }, [isAuthenticated, currentPage]);

  // Filter organizers based on search term and role filter
  // Note: With server-side pagination, filtering should ideally be done on the server
  // For now, we'll filter the current page results only
  useEffect(() => {
    console.log('Filtering organizers:', { organizers, searchTerm, roleFilter }); // Debug log
    let filtered = Array.isArray(organizers) ? organizers : [];

    // Only apply local filtering if we have search term or role filter
    // In a full implementation, these should be sent to the server
    if (searchTerm || roleFilter !== 'all') {
      // Filter by search term
      if (searchTerm) {
        filtered = filtered.filter(organizer => {
          const fullName = getFullName(organizer);
          return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            organizer.email?.toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      // Filter by role
      if (roleFilter !== 'all') {
        filtered = filtered.filter(organizer => organizer.role === roleFilter);
      }
    }

    console.log('Filtered result:', filtered); // Debug log
    setFilteredOrganizers(Array.isArray(filtered) ? filtered : []);
  }, [organizers, searchTerm, roleFilter]);

  const handleEdit = (organizerId) => {
    navigate(`/organizers/edit/${organizerId}`);
  };

  const handleDelete = (organizer) => {
    setDeleteModal({
      isOpen: true,
      organizerId: organizer.id,
      organizerName: getFullName(organizer),
      isDeleting: false
    });
  };

  const handleDeleteConfirm = async () => {
    const { organizerId } = deleteModal;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/organizer/${organizerId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'lang': getLangFromLocalStorage(),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete organizer');
      }

      // Remove from local state on successful deletion
      const updatedOrganizers = organizers.filter(org => org.id !== organizerId);
      setOrganizers(updatedOrganizers);

      // Close modal
      setDeleteModal({
        isOpen: false,
        organizerId: null,
        organizerName: '',
        isDeleting: false
      });

      console.log('Organizer deleted successfully');

    } catch (error) {
      console.error('Error deleting organizer:', error);
      // Keep modal open but stop loading state
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
      alert(t('deleteFailedMessage'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      organizerId: null,
      organizerName: '',
      isDeleting: false
    });
  };

  const handleAddOrganizer = () => {
    navigate('/organizers/add');
  };

  return (
    <div className="organizers-container">
      {/* Title */}
      <div className="organizers-header">
        <h1 className="organizers-title">{t('organizers')}</h1>
      </div>

      {/* Controls Row */}
      <div className="organizers-controls">
        <div className="controls-left">
          <h2 className="all-organizers-title">{t('allOrganizers')}</h2>
        </div>

        <div className="controls-right">
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              placeholder={t('searchOrganizers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-org"
            />
            <span className='search-icon'>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11 20.75C5.62 20.75 1.25 16.38 1.25 11C1.25 5.62 5.62 1.25 11 1.25C16.38 1.25 20.75 5.62 20.75 11C20.75 16.38 16.38 20.75 11 20.75ZM11 2.75C6.45 2.75 2.75 6.45 2.75 11C2.75 15.55 6.45 19.25 11 19.25C15.55 19.25 19.25 15.55 19.25 11C19.25 6.45 15.55 2.75 11 2.75Z" fill="#464646" />
                <path d="M20.16 22.79C20.08 22.79 20 22.78 19.93 22.77C19.46 22.71 18.61 22.39 18.13 20.96C17.88 20.21 17.97 19.46 18.38 18.89C18.79 18.32 19.48 18 20.27 18C21.29 18 22.09 18.39 22.45 19.08C22.81 19.77 22.71 20.65 22.14 21.5C21.43 22.57 20.66 22.79 20.16 22.79ZM19.56 20.49C19.73 21.01 19.97 21.27 20.13 21.29C20.29 21.31 20.59 21.12 20.9 20.67C21.19 20.24 21.21 19.93 21.14 19.79C21.07 19.65 20.79 19.5 20.27 19.5C19.96 19.5 19.73 19.6 19.6 19.77C19.48 19.94 19.46 20.2 19.56 20.49Z" fill="#464646" />
              </svg>
            </span>

            {/* <img src="/search.svg" alt={t('search')} className="search-icon" /> */}
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="role-filter"
          >
            <option value="all">{t('allRoles')}</option>
            <option value="manager">{t('manager')}</option>
            <option value="viewer">{t('viewer')}</option>
          </select>

          {/* Add Organizer Button */}
          <button onClick={handleAddOrganizer} className="add-organizer-btn">
            {/* <img src="/plus.svg" alt={t('add')} className="add-icon" /> */}
            {t('addOrganizer')}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13.3334 10.625H6.66675C6.32508 10.625 6.04175 10.3417 6.04175 10C6.04175 9.65833 6.32508 9.375 6.66675 9.375H13.3334C13.6751 9.375 13.9584 9.65833 13.9584 10C13.9584 10.3417 13.6751 10.625 13.3334 10.625Z" fill="white" />
              <path d="M10 13.9583C9.65833 13.9583 9.375 13.675 9.375 13.3333V6.66666C9.375 6.325 9.65833 6.04166 10 6.04166C10.3417 6.04166 10.625 6.325 10.625 6.66666V13.3333C10.625 13.675 10.3417 13.9583 10 13.9583Z" fill="white" />
              <path d="M12.5001 18.9583H7.50008C2.97508 18.9583 1.04175 17.025 1.04175 12.5V7.5C1.04175 2.975 2.97508 1.04166 7.50008 1.04166H12.5001C17.0251 1.04166 18.9584 2.975 18.9584 7.5V12.5C18.9584 17.025 17.0251 18.9583 12.5001 18.9583ZM7.50008 2.29166C3.65841 2.29166 2.29175 3.65833 2.29175 7.5V12.5C2.29175 16.3417 3.65841 17.7083 7.50008 17.7083H12.5001C16.3417 17.7083 17.7084 16.3417 17.7084 12.5V7.5C17.7084 3.65833 16.3417 2.29166 12.5001 2.29166H7.50008Z" fill="white" />
            </svg>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner-container">
              <img src="/loader.svg" alt={t('loading')} className="loading-spinner" />
              <p>{t('loadingOrganizers')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-content">
              <img src="/error.svg" alt={t('error')} className="error-icon" />
              <h3>{t('failedToLoadOrganizers')}</h3>
              <p>{error}</p>
              <button onClick={fetchOrganizers} className="retry-btn">
                {t('tryAgain')}
              </button>
            </div>
          </div>
        ) : organizers.length === 0 ? (
          <EmptyState
            icon="/empty-organizers.svg"
            title={t('noOrganizersYet')}
            description={t('noOrganizersDescription')}
            actionButton={
              <button onClick={handleAddOrganizer} className="empty-action-btn">
                <img src="/plus.svg" alt={t('add')} className="add-icon" />
                {t('addYourFirstOrganizer')}
              </button>
            }
          />
        ) : filteredOrganizers.length === 0 ? (
          <EmptyState
            isSearch={true}
            searchTerm={searchTerm || (roleFilter !== 'all' ? `role: ${roleFilter}` : '')}
          />
        ) : (
          <table className="organizers-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('email')}</th>
                <th>{t('role')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(filteredOrganizers) && filteredOrganizers.map((organizer) => (
                <tr key={organizer.id}>
                  <td className="organizer-name">{getFullName(organizer)}</td>
                  <td className="organizer-email">{organizer.email}</td>
                  <td>
                    <span className={`role-badge role-${organizer.role}`}>
                      {organizer.role === 'manager' ? t('manager') : t('viewer')}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      onClick={() => handleEdit(organizer.id)}
                      className="action-btn edit-btn"
                      title={t('edit')}
                    >
                      <img src="/edit.svg" alt={t('edit')} className="action-icon" />
                    </button>
                    <button
                      onClick={() => handleDelete(organizer)}
                      className="action-btn delete-btn"
                      title={t('delete')}
                    >
                      <img src="/delete.svg" alt={t('delete')} className="action-icon" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {organizers.length > 0 && totalPages > 1 && (
        <OrganizerPagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        >
          <OrganizerRenderPaginationNumbers
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </OrganizerPagination>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteModal.organizerName}
        isLoading={deleteModal.isDeleting}
      />
    </div>
  );
};

export default OrganizersTable;